import { useState, useEffect, useCallback } from "react";
import { QuickQuotes } from "@/components/webadvisor/QuickQuotes";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Loader2, Send, UserCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { fileUrl } from "@/lib/products";
import { LeadDetails } from "@/components/webadvisor/LeadDetails";
import { ShareReportForm } from "@/components/webadvisor/ShareReportForm";
import { SharedReportsList } from "@/components/webadvisor/SharedReportsList";
import { ProposalBuilder } from "@/components/webadvisor/ProposalBuilder";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";
import { ClientHistory } from "@/components/webadvisor/ClientHistory";

const Card = ({ title, sub, children, testId }) => (
  <section className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testId}>
    <h2 className="font-bold text-slate-900">{title}</h2>
    {sub && <p className="text-xs text-slate-500 mb-4">{sub}</p>}
    {children}
  </section>
);

export default function CustomerProfilePage() {
  const { leadId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/api/webadvisor/leads/${leadId}/profile`).then(r => setD(r.data)).catch(() => setD(false)), [leadId]);
  useEffect(() => { load(); }, [load]);

  if (d === false) return <div className="pt-28 text-center text-slate-500" data-testid="profile-not-found">Lead not found. <Link to="/webadvisor/leads" className="text-blue-600">Back to pipeline</Link></div>;
  if (!d) return <div className="pt-28 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const { lead, customer } = d;
  const dropDoc = (id) => setD(x => ({ ...x, documents: x.documents.filter(o => o.id !== id) }));
  const setDoc = (doc) => setD(x => ({ ...x, documents: x.documents.some(o => o.id === doc.id) ? x.documents.map(o => (o.id === doc.id ? doc : o)) : [doc, ...x.documents] }));

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-16 px-4 sm:px-6" data-testid="customer-profile-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/webadvisor/leads" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="profile-back-link"><ArrowLeft className="w-4 h-4" />Pipeline</Link>
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {lead.logo_url ? <img src={fileUrl(lead.logo_url)} alt="" className="w-14 h-14 object-contain rounded-xl bg-white border border-slate-200 p-1" /> : <UserCircle2 className="w-14 h-14 text-slate-300" />}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 break-words" data-testid="profile-title">{lead.business_name || lead.name || (lead.website_url || "").replace(/^https?:\/\//, "")}</h1>
            <p className="text-sm text-slate-500 break-all">{lead.audit_id ? `${lead.website_url} · score ${lead.website_score}` : lead.source} · <span className="font-medium text-slate-700" data-testid="profile-status">{lead.status}</span>{customer ? ` · portal login ${customer.email}` : ""}</p>
          </div>
          {lead.audit_id && <Link to={`/webadvisor/audit/${lead.audit_id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline" data-testid="profile-open-audit">Open audit <ExternalLink className="w-3.5 h-3.5" /></Link>}
          {isAdmin && <ConfirmDelete url={`/api/webadvisor/leads/${lead.id}`} label="Delete lead" title="Delete this lead?" description="Removes the lead with its audits, growth plans, invoices, payments, landing pages and shared reports. The customer login (if any) stays." onDone={() => navigate("/webadvisor/leads")} testId="profile-delete-lead" className="border border-red-200 px-3 py-1.5" />}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,380px)_1fr] gap-6 items-start">
          <div className="space-y-6">
            <Card title="Contact & account" testId="profile-details-card">
              <div className="mt-4"><LeadDetails lead={lead} isAdmin={isAdmin} onSaved={() => load()} /></div>
            </Card>
            <Card title="History" sub="Everything sent to and from this client — emails, portal documents, views, signatures, payments and messages." testId="profile-history-card">
              <ClientHistory leadId={lead.id} refreshKey={d.documents.length + d.documents.filter(x => x.visible).length} />
            </Card>
          </div>
          <div className="space-y-6">
            {lead.audit_id && <Card title="Quick quote or invoice" sub="Turn the audit straight into a priced quote or invoice — no AI plan needed." testId="profile-quick-card">
              <QuickQuotes leadId={lead.id} isAdmin={isAdmin} isCustomer={!!customer} onSent={() => load()} />
            </Card>}
            <Card title="Proposal builder" sub="Run the audit, let AI build the growth plan, set the quote, then send it all to the customer's portal. Update and resend any time." testId="profile-proposal-card">
              <ProposalBuilder lead={lead} isAdmin={isAdmin} isCustomer={!!customer} onSent={() => load()} />
            </Card>
            {customer ? (
              <>
                <Card title="Send a single document" sub="Pick a report, invoice or statement. It appears under Reports in their portal, and you can email them too." testId="profile-share-card">
                  <ShareReportForm leadId={lead.id} options={d.options} onShared={setDoc} />
                </Card>
                <Card title={`Shared with customer (${d.documents.filter(x => x.visible).length} visible)`} sub="Switch off anything sent by mistake or replaced by a newer version. The customer stops seeing it right away." testId="profile-shared-card">
                  <SharedReportsList docs={d.documents} onChange={setDoc} isAdmin={isAdmin} onDeleted={dropDoc} />
                </Card>
              </>
            ) : (
              <Card title="Customer portal" testId="profile-portal-locked">
                <p className="text-sm text-slate-500 mt-2 flex items-start gap-2"><Send className="w-4 h-4 mt-0.5 shrink-0" />Turn this lead into a customer to send reports to their portal.</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
